import type { Config, OverridedMixpanel } from 'mixpanel-browser';

import type { AnalyticsPayload } from './events';

type MixpanelCall = (mixpanel: OverridedMixpanel) => void;

type PendingEvent = {
	name: string;
	properties: AnalyticsPayload;
	/** When it was tracked, so a late replay keeps the original timestamp. */
	time: number;
};

/**
 * - `waiting`: no `initMixpanel` yet — calls are kept, a screen may track on mount.
 * - `loading`: the SDK chunk is downloading — calls are kept.
 * - `ready`: calls go straight to the SDK.
 * - `off`: no token, or the SDK failed to load — calls are dropped.
 */
let status: 'waiting' | 'loading' | 'ready' | 'off' = 'waiting';
let instance: OverridedMixpanel | null = null;
let pendingCalls: MixpanelCall[] = [];

/**
 * Events tracked before the SDK is ready are persisted, not only queued in
 * memory: these screens often track right before Auth0 navigates away (e.g.
 * submitLoginIdForm), which would drop an in-memory queue. The next screen
 * replays them once its SDK loads.
 */
const PENDING_EVENTS_KEY = 'prophero:analytics:pending-events';
/** Bounds storage if the SDK keeps failing to load (blocked by an extension, say). */
const MAX_PENDING_EVENTS = 50;

function readPendingEvents(): PendingEvent[] {
	try {
		const stored: unknown = JSON.parse(localStorage.getItem(PENDING_EVENTS_KEY) ?? '[]');
		return Array.isArray(stored) ? (stored as PendingEvent[]) : [];
	} catch {
		return [];
	}
}

function writePendingEvents(events: PendingEvent[]): void {
	try {
		if (events.length) {
			localStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(events.slice(-MAX_PENDING_EVENTS)));
		} else {
			localStorage.removeItem(PENDING_EVENTS_KEY);
		}
	} catch {
		// Storage unavailable: the event is lost, as it would be without the SDK.
	}
}

/** Runs a call on the SDK, or keeps it until the SDK is ready. */
function withMixpanel(call: MixpanelCall): void {
	if (status === 'off') return;
	if (instance) {
		call(instance);
		return;
	}
	pendingCalls.push(call);
}

async function loadMixpanel(
	token: string,
	isDev: boolean,
	options?: Partial<Config>,
): Promise<void> {
	const { default: sdk } = await import('mixpanel-browser/src/loaders/loader-module-core');
	// The loader's typings re-export the named types but not the default instance.
	const mixpanel = sdk as unknown as OverridedMixpanel;
	mixpanel.init(token, {
		persistence: 'localStorage',
		autocapture: true,
		debug: isDev,
		api_host: 'https://api-eu.mixpanel.com',
		...options,
	});
	instance = mixpanel;
	status = 'ready';

	const events = readPendingEvents();
	writePendingEvents([]);
	events.forEach(({ name, properties, time }) => {
		mixpanel.track(name, { ...properties, time: Math.round(time / 1000) });
	});

	const calls = pendingCalls;
	pendingCalls = [];
	calls.forEach((call) => call(mixpanel));
}

/**
 * Initialises the Mixpanel browser SDK. Must be called once at app startup.
 * No-ops on duplicate calls so screens that re-mount are safe.
 *
 * The SDK (~105 KB gzip with its session recorder) is a separate chunk loaded
 * on demand rather than part of the bundle every screen downloads before
 * showing its form (CX-902). The core build is enough — the screens never
 * record sessions. Calls made before it is ready, including before this is
 * called, are kept and replayed in order.
 *
 * @param token - Mixpanel project token.
 * @param options - Optional overrides for `mixpanel.init()`.
 */
export function initMixpanel(token: string, isDev: boolean, options?: Partial<Config>): void {
	if (status !== 'waiting') return;
	if (!token) {
		// No analytics in this environment (e.g. local dev): forget what was kept.
		status = 'off';
		pendingCalls = [];
		writePendingEvents([]);
		return;
	}
	status = 'loading';

	void loadMixpanel(token, isDev, options).catch(() => {
		// Analytics must never break sign-in. Persisted events stay for the
		// next screen to retry; in-memory calls are dropped.
		status = 'off';
		pendingCalls = [];
	});
}

/**
 * Tracks an event in Mixpanel. Before the SDK is ready the event is persisted
 * and sent once it is, on this screen or the next one.
 */
export function track(eventName: string, properties?: AnalyticsPayload): void {
	if (status === 'off') return;
	if (instance) {
		instance.track(eventName, properties ?? {});
		return;
	}
	writePendingEvents([
		...readPendingEvents(),
		{ name: eventName, properties: properties ?? {}, time: Date.now() },
	]);
}

/**
 * Associates the current anonymous Mixpanel profile with a known user ID.
 * Call after login once the Auth0 `sub` claim is available.
 */
export function identify(distinctId: string): void {
	if (!distinctId) return;
	withMixpanel((mixpanel) => {
		mixpanel.identify(distinctId);
	});
}

/**
 * Sets persistent properties on the identified user's Mixpanel profile.
 */
export function setUserProperties(properties: Record<string, unknown>): void {
	withMixpanel((mixpanel) => {
		mixpanel.people.set({
			$name: properties.name,
			$email: properties.email,
		});
		mixpanel.register({ $email: properties.email, $name: properties.name });
	});
}

/**
 * Clears the current user's identity and generates a new anonymous
 * distinct_id. Call on logout.
 */
export function resetIdentity(): void {
	withMixpanel((mixpanel) => {
		mixpanel.reset();
	});
}

/** @internal For tests only. */
export function resetMixpanelForTests(): void {
	status = 'waiting';
	instance = null;
	pendingCalls = [];
	writePendingEvents([]);
}
